import { Timestamp } from 'firebase/firestore';

import { EVENT_SCORE_STATUSES } from '../types/EventScore';

import type Event from '../types/Event';
import type { EventScore } from '../types/EventScore';
import type { ScoreRowPlayers } from 'shared/dist/utils/scoreRows';

/** Decode a stored EventScore hole value to the display string used in score entry. */
export function decodeScore(n: number): string {
  if (!n) return '';
  if (n === 25) return '½';
  if (n === 50) return '+';
  if (n === -50) return '-';
  if (n === -99) return 'NS';
  if (n === -1) return 'x';
  if (n < 0) return `x${Math.abs(n)}`;
  return String(n);
}

/** Encode a display string from score entry to the stored EventScore hole value. */
export function encodeScore(val: string): number {
  if (!val || val === '1/') return 0;
  if (val === '½' || val === '1/2') return 25;
  if (val === '+') return 50;
  if (val === '-') return -50;
  if (val.toUpperCase() === 'NS') return -99;
  // Case-insensitive so imported files using "X6" (as well as our own "x6") both work.
  const lower = val.toLowerCase();
  if (lower === 'x') return -1;
  if (lower.startsWith('x')) return -(parseInt(lower.slice(1), 10) || 1);
  // "5x" is the same most likely score as "x5" — without this, parseInt reads it as a finished 5.
  if (/^\d+x$/.test(lower)) return -(parseInt(lower, 10) || 1);
  return parseInt(val, 10) || 0;
}

/**
 * Returns the stroke count contribution of a stored hole value for subtotal display.
 * Bare 'x' (-1) contributes 0 since no score was recorded.
 * 'x{n}' contributes the estimated n strokes.
 */
export function holeStroke(encoded: number): number {
  if (encoded > 0 && encoded !== 25 && encoded !== 50) return encoded;
  if (encoded < -1 && encoded !== -50 && encoded !== -99) return Math.abs(encoded);
  return 0;
}

// ─── Score doc writing ────────────────────────────────────────────────────────
// Every client-side writer of an EventScore doc (score grid, CSV import, measurement entry) builds
// it with buildEventScore and saves it with useEventScoreMutator.saveRowScore. The row helpers live
// in shared (shared/utils/scoreRows.ts) and are re-exported here for client code.
export { findRowScore, planRowScoreCleanup } from 'shared/dist/utils/scoreRows';
export type { ScoreRowPlayers } from 'shared/dist/utils/scoreRows';

type ScoreFields = Partial<Pick<EventScore, 'scoreByHole' | 'totalScore' | 'entryMode' | 'result' | 'measurementsByHole'>>;

/**
 * Builds the EventScore doc to save for one score-entry row. Starts from `existing` (keeping
 * overrides, result, publish state and anything else already on it) or a fresh DRAFT doc, applies
 * `fields`, and ALWAYS sets playerIds/playerNames from `row` — which also repairs an existing doc
 * naming fewer players than its row — and the row's scoringTypeKey. teeId follows the row's first member's assignment (one shared
 * tee per multi-player row, matching the results engine), falling back to whatever the doc had.
 */
export function buildEventScore(
  event: Pick<Event, 'orgId' | 'leagueId' | 'seasonId' | 'itemId' | 'days'>,
  dayIndex: number,
  row: ScoreRowPlayers,
  existing: EventScore | undefined,
  teeAssignments: Record<string, string> | undefined,
  fields: ScoreFields,
): EventScore {
  // teeId is conditionally spread, never assigned undefined -- the client Firestore instance
  // doesn't ignore undefined properties and throws on save. Every score belongs to its day by the
  // day's stable itemId (shared/utils/dayIdentity.ts); every saved day has one.
  const dayId = event.days[dayIndex]?.itemId;
  if (!dayId) throw new Error(`Day ${dayIndex + 1} has no id yet — save the event before entering scores`);
  const teeId = teeAssignments?.[row.playerIds[0] ?? ''] ?? existing?.teeId;
  const base: EventScore = existing ?? {
    itemId: '',
    orgId: event.orgId,
    leagueId: event.leagueId,
    seasonId: event.seasonId,
    eventId: event.itemId!,
    dayIndex,
    dayId,
    playerIds: [],
    playerNames: [],
    scoreByHole: [],
    totalScore: 0,
    status: EVENT_SCORE_STATUSES.DRAFT,
    verifiedBy: null,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  return {
    ...base,
    dayId,
    ...(teeId ? { teeId } : {}),
    ...fields,
    playerIds: row.playerIds,
    playerNames: row.playerNames,
    ...(row.scoringTypeKey ? { scoringTypeKey: row.scoringTypeKey } : {}),
    updatedAt: Timestamp.now(),
  };
}
